import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  ArrowsClockwise,
  ArrowRight,
  Brain,
  CaretRight,
  CheckCircle,
  Clock,
  CloudArrowUp,
  Envelope,
  EnvelopeSimple,
  FileText,
  FolderSimple,
  MagnifyingGlass,
  Plus,
  PencilSimple,
  ShieldCheck,
  Sparkle,
  Trash,
  Warning,
  X,
} from "@phosphor-icons/react";
import { Button } from "../../../components/ui/button";
import SmartUploadPanel from "../../../components/files/SmartUploadPanel";
import {
  getProjectEmailLogs, getEmailBody, getTrainingLog, removeTrainingEntry,
  getRoutingNotes, createRoutingNote, updateRoutingNote, deleteRoutingNote,
  getRoutingNoteHistory, getRoutingTraces,
} from "../../../api/projects";

const ROUTING_TYPES = {
  alias: "Alias", job_code: "Job code", drawing_prefix: "Drawing prefix",
  positive: "Positive", negative: "Negative", exception: "Exception",
};

const EMPTY_NOTE = { note_type: "alias", content: "", effect: "negative" };

export function RoutingNotesSection({ projectId }) {
  const [expanded, setExpanded] = useState(false);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(EMPTY_NOTE);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(null);
  const [histories, setHistories] = useState({});
  const [historyLoading, setHistoryLoading] = useState(null);

  const reload = useCallback(async () => {
    try { setNotes(await getRoutingNotes(projectId)); }
    catch { setNotes([]); }
    finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { reload(); }, [reload]);

  const startAdd = () => { setEditing("new"); setDraft(EMPTY_NOTE); setExpanded(true); };
  const startEdit = (note) => {
    setEditing(note.id);
    setDraft({ note_type: note.note_type, content: note.content, effect: note.effect || "negative" });
  };
  const save = async () => {
    if (!draft.content.trim()) return;
    setSaving(true);
    const body = { note_type: draft.note_type, content: draft.content.trim(), ...(draft.note_type === "exception" ? { effect: draft.effect } : {}) };
    try {
      if (editing === "new") {
        const created = await createRoutingNote(projectId, body);
        setNotes((current) => [...current, created]);
      } else {
        const existing = notes.find((note) => note.id === editing);
        const updated = await updateRoutingNote(projectId, editing, { ...body, expected_revision: existing.revision });
        setNotes((current) => current.map((note) => note.id === editing ? updated : note));
      }
      setEditing(null);
      setDraft(EMPTY_NOTE);
      toast.success("Routing note saved.");
    } catch (error) {
      if (error.response?.status === 409) await reload();
      toast.error(error.response?.data?.detail || "Failed to save routing note.");
    } finally { setSaving(false); }
  };
  const remove = async (note) => {
    setSaving(true);
    try {
      await deleteRoutingNote(projectId, note.id, note.revision);
      setNotes((current) => current.filter((item) => item.id !== note.id));
      setConfirmDelete(null);
      toast.success("Routing note removed from future matching.");
    } catch (error) {
      if (error.response?.status === 409) await reload();
      toast.error(error.response?.data?.detail || "Failed to remove routing note.");
    } finally { setSaving(false); }
  };
  const toggleHistory = async (note) => {
    if (historyOpen === note.id) { setHistoryOpen(null); return; }
    setHistoryOpen(note.id);
    if (histories[note.id]) return;
    setHistoryLoading(note.id);
    try {
      const result = await getRoutingNoteHistory(projectId, note.id);
      setHistories((current) => ({ ...current, [note.id]: result.history || [] }));
    } catch {
      setHistories((current) => ({ ...current, [note.id]: [] }));
    } finally { setHistoryLoading(null); }
  };

  return (
    <section id="routing-notes" className="bg-white border border-slate-200 rounded-sm overflow-hidden scroll-mt-24" data-testid="routing-notes-section">
      <button type="button" aria-expanded={expanded} onClick={() => setExpanded((value) => !value)} className="w-full flex items-center gap-2 px-5 py-3 text-left hover:bg-slate-50 transition-colors" data-testid="routing-notes-toggle">
        <CaretRight size={12} className={`text-slate-400 transition-transform ${expanded ? "rotate-90" : ""}`} />
        <Brain size={13} className="text-slate-400" />
        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Email routing notes</span>
        {!loading && <span className="text-[10px] text-slate-400">{notes.length}</span>}
        <span className="ml-auto text-[10px] text-slate-400">Optional routing memory</span>
      </button>
      {expanded && <div className="border-t border-slate-100 px-5 py-4 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <p className="text-xs text-slate-400 max-w-xl">Aliases, codes and exceptions guide matching. They cannot independently force an automatic route or skip.</p>
          <Button type="button" size="sm" variant="outline" onClick={startAdd} className="h-7 text-xs" data-testid="add-routing-note"><Plus size={11} className="mr-1" /> Add</Button>
        </div>
        {editing && <div className="rounded border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid="routing-note-editor">
          {editing !== "new" && notes.find((note) => note.id === editing)?.owner === "ai" && <p className="text-[11px] text-violet-700 bg-violet-50 border border-violet-100 rounded px-2 py-1.5">Saving makes this note yours. AI will no longer be able to change or remove it.</p>}
          <div className="grid grid-cols-1 sm:grid-cols-[150px_1fr] gap-2">
            <select aria-label="Routing note type" value={draft.note_type} onChange={(event) => setDraft({ ...draft, note_type: event.target.value })} className="h-9 rounded border border-slate-200 bg-white px-2 text-xs">
              {Object.entries(ROUTING_TYPES).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
            <input aria-label="Routing note content" maxLength={500} value={draft.content} onChange={(event) => setDraft({ ...draft, content: event.target.value })} className="h-9 rounded border border-slate-200 bg-white px-3 text-sm" placeholder="Project alias, job code or routing instruction" />
          </div>
          {draft.note_type === "exception" && <label className="flex items-center gap-2 text-xs text-slate-600">Effect <select value={draft.effect} onChange={(event) => setDraft({ ...draft, effect: event.target.value })} className="h-8 rounded border border-slate-200 bg-white px-2"><option value="negative">Negative</option><option value="positive">Positive</option></select></label>}
          <div className="flex items-center justify-between"><span className="text-[10px] text-slate-400">{draft.content.length}/500</span><div className="flex gap-2"><Button type="button" size="sm" variant="ghost" onClick={() => setEditing(null)} className="h-7 text-xs">Cancel</Button><Button type="button" size="sm" onClick={save} disabled={saving || !draft.content.trim()} className="h-7 text-xs">{saving ? "Saving…" : "Save note"}</Button></div></div>
        </div>}
        {!loading && notes.length === 0 && !editing && <div className="text-center py-4"><p className="text-xs text-slate-400">No routing notes yet.</p><button type="button" onClick={startAdd} className="text-xs text-violet-600 hover:underline mt-1">Add the first note</button></div>}
        <div className="divide-y divide-slate-100">
          {notes.map((note) => <div key={note.id} className={`py-3 ${note.owner === "ai" ? "border-l-2 border-violet-300 pl-3" : ""}`} data-testid={`routing-note-${note.id}`}>
            <div className="flex items-start gap-2">
              <span className="text-[9px] font-semibold uppercase tracking-wide text-slate-500 bg-slate-100 rounded px-1.5 py-0.5">{ROUTING_TYPES[note.note_type]}</span>
              <span className={`text-[9px] font-semibold rounded border px-1.5 py-0.5 ${note.owner === "ai" ? "bg-violet-50 border-violet-100 text-violet-700" : "bg-blue-50 border-blue-100 text-blue-700"}`}>{note.owner === "ai" ? "AI learned" : note.origin === "ai" ? "Edited by you" : "Added by you"}</span>
              <p className="flex-1 text-xs text-slate-700 whitespace-pre-wrap break-words">{note.content}</p>
              <button type="button" aria-label="Edit routing note" onClick={() => startEdit(note)} className="text-slate-400 hover:text-slate-700"><PencilSimple size={12} /></button>
              <button type="button" aria-label="Delete routing note" onClick={() => setConfirmDelete(note.id)} className="text-slate-400 hover:text-red-600"><Trash size={12} /></button>
            </div>
            <button type="button" aria-expanded={historyOpen === note.id} onClick={() => toggleHistory(note)} className="mt-1 text-[10px] text-slate-400 hover:text-slate-600">Why is this here?</button>
            {historyOpen === note.id && <div className="mt-1 rounded bg-slate-50 px-2 py-2 text-[10px] text-slate-500 space-y-1" data-testid={`routing-note-history-${note.id}`}>
              <p>{note.provenance?.reason || (note.origin === "ai" ? "Learned from grounded routing evidence." : "Created by you.")}</p>
              {historyLoading === note.id ? <p>Loading history…</p> : (histories[note.id] || []).slice(-3).reverse().map((event) => <div key={event.event_id} className="flex items-start gap-1.5 border-t border-slate-200 pt-1">
                <span className="font-semibold capitalize">{event.action}</span>
                <span>by {event.actor_type === "ai" ? "AI" : "you"}</span>
                <span>· {new Date(event.at).toLocaleString("en-AU")}</span>
                {event.ownership_transferred && <span className="text-blue-600">· ownership transferred to you</span>}
                {event.reason && <span className="truncate">· {event.reason}</span>}
              </div>)}
            </div>}
            {confirmDelete === note.id && <div role="alertdialog" aria-label="Confirm routing note deletion" className="mt-2 flex items-center gap-2 rounded bg-red-50 border border-red-100 px-2 py-2 text-[11px] text-red-700"><span className="flex-1">Remove from future routing? Existing routed emails are unchanged.</span><button type="button" onClick={() => setConfirmDelete(null)} className="font-medium">Cancel</button><button type="button" disabled={saving} onClick={() => remove(note)} className="font-semibold">Remove</button></div>}
          </div>)}
        </div>
      </div>}
    </section>
  );
}

function fmtRelativeLog(iso) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short" });
}

export function EmailActivityCard({ projectId, project, msConnected, onSyncInbox }) {
  const [emailLogs, setEmailLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [cursor, setCursor] = useState(0);
  const [total, setTotal] = useState(0);
  const [expandedId, setExpandedId] = useState(null);
  const [emailBody, setEmailBody] = useState(null);
  const [bodyLoading, setBodyLoading] = useState(false);
  const [bodyError, setBodyError] = useState(null);
  const PAGE_SIZE = 20;

  const fetchLogs = useCallback(async (searchVal = "", cursorVal = 0, append = false) => {
    setLogsLoading(true);
    try {
      const data = await getProjectEmailLogs(projectId, { search: searchVal || undefined, pageSize: PAGE_SIZE, cursor: cursorVal });
      if (append) {
        setEmailLogs((prev) => [...prev, ...(data.items || [])]);
      } else {
        setEmailLogs(data.items || []);
      }
      setTotal(data.total || 0);
      setCursor(cursorVal);
    } catch {
      if (!append) setEmailLogs([]);
    } finally {
      setLogsLoading(false);
    }
  }, [projectId]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const handleSearch = () => {
    setSearch(searchInput);
    fetchLogs(searchInput, 0);
  };

  const handleClearSearch = () => {
    setSearchInput("");
    setSearch("");
    fetchLogs("", 0);
  };

  const handleLoadMore = () => {
    const nextCursor = cursor + PAGE_SIZE;
    fetchLogs(search, nextCursor, true);
  };

  const handleExpand = async (log) => {
    const intakeId = log.intake_id;
    if (expandedId === intakeId) {
      setExpandedId(null);
      setEmailBody(null);
      setBodyError(null);
      return;
    }
    setExpandedId(intakeId);
    setEmailBody(null);
    setBodyError(null);
    setBodyLoading(true);
    try {
      const data = await getEmailBody(projectId, intakeId);
      setEmailBody(data);
    } catch (err) {
      const detail = err.response?.data?.detail || "Unable to load email body — Microsoft connection may have expired";
      setBodyError(detail);
    } finally {
      setBodyLoading(false);
    }
  };

  const canSync = msConnected
    && (project?.client_emails || []).length > 0
    && !!project?.onedrive_folder_id
    && project?.backfill_status !== "running";

  const handleSync = async () => {
    setSyncing(true);
    try {
      await onSyncInbox?.();
    } finally {
      setSyncing(false);
    }
  };

  const hasMore = emailLogs.length < total;

  return (
    <section data-testid="email-activity-section" className="bg-white border border-slate-200 rounded-sm p-5">
      <header className="flex items-center gap-2 mb-3">
        <EnvelopeSimple size={14} className="text-violet-500" weight="fill" />
        <h3 className="text-[10px] font-bold uppercase tracking-widest text-slate-500 flex-1">Email Activity</h3>
        {canSync && (
          <button
            onClick={handleSync}
            disabled={syncing}
            className="flex items-center gap-1 text-[10px] font-semibold text-violet-600 hover:text-violet-800 disabled:opacity-50 transition-colors uppercase tracking-wider"
            data-testid="sync-inbox-btn"
            title="Search inbox for past emails and import matching ones"
          >
            {syncing ? <ArrowsClockwise size={10} className="animate-spin" /> : <MagnifyingGlass size={10} />}
            Sync from Inbox
          </button>
        )}
      </header>

      {/* Search input */}
      <div className="flex items-center gap-2 mb-3">
        <div className="relative flex-1">
          <MagnifyingGlass size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Search by subject or sender…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            className="w-full h-8 pl-7 pr-8 rounded border border-slate-200 bg-white text-xs text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-violet-300"
            data-testid="email-search-input"
          />
          {searchInput && (
            <button onClick={handleClearSearch} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" aria-label="Clear search">
              <X size={12} />
            </button>
          )}
        </div>
        <button onClick={handleSearch} className="h-8 px-3 rounded border border-slate-200 bg-slate-50 text-xs text-slate-600 hover:bg-slate-100" data-testid="email-search-btn">
          Search
        </button>
      </div>

      {logsLoading && emailLogs.length === 0 ? (
        <div className="flex items-center gap-2 text-slate-400 py-2">
          <ArrowsClockwise size={12} className="animate-spin" />
          <span className="text-xs">Loading…</span>
        </div>
      ) : emailLogs.length === 0 ? (
        <p className="text-xs text-slate-400 py-2">{search ? "No emails match your search." : "No emails routed to this project yet."}</p>
      ) : (
        <div className="space-y-1.5">
          {emailLogs.map((log, i) => (
            <div key={log.intake_id || i} className="flex flex-col gap-0 bg-slate-50 border border-slate-100 rounded-sm text-xs overflow-hidden" data-testid={`email-log-row-${i}`}>
              {/* Clickable header row */}
              <button
                type="button"
                onClick={() => handleExpand(log)}
                className="flex items-start gap-2.5 p-2.5 w-full text-left hover:bg-slate-100 transition-colors"
                data-testid={`email-log-expand-${i}`}
                aria-expanded={expandedId === log.intake_id}
              >
                <CaretRight size={11} className={`text-slate-400 flex-shrink-0 mt-1 transition-transform ${expandedId === log.intake_id ? "rotate-90" : ""}`} />
                <Envelope size={13} className="text-violet-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-slate-800 truncate">{log.email_subject || "(no subject)"}</p>
                  <p className="text-slate-400 text-[10px] truncate">From: {log.from_email}</p>
                  {log.reason && <p className="text-slate-400 text-[10px] italic truncate">{log.reason}</p>}
                  {log.decision_reason && <p className="text-violet-500 text-[10px] mt-0.5" data-testid={`email-decision-reason-${i}`}>{log.decision_reason}</p>}
                  {(log.routing_note_ids?.length > 0 || log.user_decision?.action) && (
                    <p className="text-[10px] text-slate-400 mt-0.5">
                      {log.routing_note_ids?.length > 0 ? `${log.routing_note_ids.length} routing note${log.routing_note_ids.length === 1 ? "" : "s"} used` : ""}
                      {log.routing_note_ids?.length > 0 && log.user_decision?.action ? " · " : ""}
                      {log.user_decision?.action ? `User ${log.user_decision.action}` : ""}
                    </p>
                  )}
                </div>
                <div className="flex flex-col items-end gap-0.5 flex-shrink-0">
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-sm ${log.confidence >= 0.85 ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                    {Math.round(log.confidence * 100)}%
                  </span>
                  <span className="text-[10px] text-slate-400 flex items-center gap-0.5">
                    <Clock size={9} />
                    {new Date(log.routed_at).toLocaleDateString()}
                  </span>
                </div>
              </button>

              {/* Expanded email body */}
              {expandedId === log.intake_id && (
                <div className="border-t border-slate-200 px-4 py-3" data-testid={`email-body-panel-${i}`}>
                  {bodyLoading ? (
                    <div className="flex items-center gap-2 text-slate-400 py-3">
                      <ArrowsClockwise size={12} className="animate-spin" />
                      <span className="text-xs">Loading email body…</span>
                    </div>
                  ) : bodyError ? (
                    <div className="flex items-center gap-2 py-3">
                      <Warning size={13} className="text-amber-500 flex-shrink-0" />
                      <p className="text-xs text-amber-700">{bodyError}</p>
                    </div>
                  ) : emailBody ? (
                    <div className="space-y-3">
                      <div
                        className="prose prose-sm max-w-none text-xs text-slate-700 overflow-auto max-h-96 border border-slate-100 rounded p-3 bg-white [&_a]:text-blue-600 [&_a]:underline [&_img]:max-w-full"
                        style={{ isolation: "isolate" }}
                        dangerouslySetInnerHTML={{ __html: emailBody.body_html }}
                        data-testid={`email-body-content-${i}`}
                      />
                      {emailBody.attachments && emailBody.attachments.length > 0 && (
                        <div className="space-y-1 pt-2 border-t border-slate-100" data-testid={`email-attachments-${i}`}>
                          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Attachments</p>
                          {emailBody.attachments.map((att, j) => (
                            <div key={j} className="flex items-center gap-2 text-[11px] text-slate-600">
                              <FileText size={11} className="text-slate-400 flex-shrink-0" />
                              <span className="truncate">{att.name}</span>
                              <span className="text-slate-400 flex-shrink-0">{att.size > 1024 * 1024 ? `${(att.size / (1024 * 1024)).toFixed(1)} MB` : `${Math.round(att.size / 1024)} KB`}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : null}
                </div>
              )}

              {/* Attachment routings (existing feature) */}
              {log.attachment_routings && log.attachment_routings.length > 0 && expandedId !== log.intake_id && (
                <div className="ml-[22px] px-2.5 pb-2 pt-1.5 border-t border-slate-200 space-y-1" data-testid={`attachment-routings-${i}`}>
                  {log.attachment_routings.map((att, j) => {
                    const isError = !!att.error;
                    const isSkipped = att.classification === "skipped";
                    const isDrawing = att.classification === "drawing";
                    const isReplaced = isDrawing && att.saved_to && att.saved_to.includes("replaced");
                    const isNewDraw = isDrawing && att.saved_to && att.saved_to.includes("new");
                    const isArchivedOlder = isDrawing && att.saved_to && att.saved_to.includes("archived older");
                    const icon = isError || isSkipped
                      ? <Warning size={11} className={isError ? "text-rose-500" : "text-amber-500"} weight="fill" />
                      : isReplaced
                        ? <ArrowsClockwise size={11} className="text-amber-600" weight="bold" />
                        : isNewDraw
                          ? <Plus size={11} className="text-emerald-600" weight="bold" />
                          : isArchivedOlder
                            ? <Clock size={11} className="text-slate-400" weight="bold" />
                            : <FileText size={11} className="text-slate-400" />;
                    const destLabel = isError
                      ? "error"
                      : isSkipped
                        ? "skipped"
                        : isArchivedOlder
                          ? "archived (older version)"
                          : isDrawing
                            ? (isReplaced ? "drawings/ (replaced)" : "drawings/ (new)")
                            : "project root";
                    return (
                      <div key={j} className="flex items-center gap-1.5 text-[10px]" data-testid={`attachment-routing-${i}-${j}`}>
                        {icon}
                        {att.saved_to_url ? (
                          <a href={att.saved_to_url} target="_blank" rel="noopener noreferrer" className="text-slate-700 hover:text-blue-600 hover:underline truncate max-w-[40%] cursor-pointer" title={`Open ${att.filename} in OneDrive`} data-testid={`attachment-routing-link-${i}-${j}`} onClick={(e) => e.stopPropagation()}>
                            {att.filename}
                          </a>
                        ) : (
                          <span className="text-slate-700 truncate max-w-[40%]" title={att.filename}>{att.filename}</span>
                        )}
                        <span className="text-slate-300">→</span>
                        <span className={`px-1.5 py-0.5 rounded font-medium ${isError ? "bg-rose-50 text-rose-700 border border-rose-100" : isSkipped ? "bg-amber-50 text-amber-700 border border-amber-100" : isReplaced ? "bg-amber-50 text-amber-700 border border-amber-100" : isNewDraw ? "bg-emerald-50 text-emerald-700 border border-emerald-100" : isArchivedOlder ? "bg-slate-100 text-slate-500 border border-slate-200" : "bg-slate-100 text-slate-600 border border-slate-200"}`}>
                          {destLabel}
                        </span>
                        {att.matched && (
                          att.matched_url ? (
                            <a href={att.matched_url} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-blue-600 hover:underline italic truncate cursor-pointer" title={isArchivedOlder ? `Current (newer) drawing kept: ${att.matched}` : `Open archived previous version: ${att.matched}`} data-testid={`attachment-prev-link-${i}-${j}`} onClick={(e) => e.stopPropagation()}>
                              {isArchivedOlder ? `· kept: ${att.matched}` : `· prev: ${att.matched}`}
                            </a>
                          ) : (
                            <span className="text-slate-400 italic truncate" title={isArchivedOlder ? `Current (newer) drawing kept: ${att.matched}` : `Archived as previous version of: ${att.matched}`}>
                              {isArchivedOlder ? `· kept: ${att.matched}` : `· prev: ${att.matched}`}
                            </span>
                          )
                        )}
                        {att.error && <span className="text-rose-600 italic truncate" title={att.error}>· {att.error}</span>}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}

          {/* Load More button */}
          {hasMore && (
            <div className="flex justify-center pt-2">
              <button
                onClick={handleLoadMore}
                disabled={logsLoading}
                className="flex items-center gap-1.5 text-xs font-medium text-violet-600 hover:text-violet-800 disabled:opacity-50 px-3 py-1.5 rounded border border-violet-200 hover:bg-violet-50 transition-colors"
                data-testid="email-load-more-btn"
              >
                {logsLoading ? <ArrowsClockwise size={11} className="animate-spin" /> : null}
                {logsLoading ? "Loading…" : `Load more (${total - emailLogs.length} remaining)`}
              </button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

export function XeroSyncBanner({ project, onRetry, retrying }) {
  const status = project.xero_sync_status;
  if (!status) return null;
  const action = project.xero_sync_action; // 'create' | 'update' | 'mark_sent'

  if (status === "pending") {
    const pendingText = action === "mark_sent"
      ? { title: "Marking invoice as sent in Xero…", detail: "Flagging this project's Xero invoice as sent to the client." }
      : action === "update"
      ? { title: "Updating Xero invoice…", detail: "Updating Xero contact and invoice for this project." }
      : { title: "Creating Xero invoice…", detail: "Creating Xero contact and invoice for this project." };
    return (
      <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-sm px-5 py-4" data-testid="xero-sync-banner-pending">
        <ArrowsClockwise size={15} className="text-blue-500 animate-spin flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-blue-800">{pendingText.title}</p>
          <p className="text-xs text-blue-600 mt-0.5">{pendingText.detail}</p>
        </div>
      </div>
    );
  }

  if (status === "synced" || status === "paid") {
    const successText = status === "paid"
      ? { title: "Xero invoice paid", detail: "Payment confirmed via Xero webhook — project marked Completed: Invoice Paid." }
      : action === "mark_sent"
      ? { title: "Xero invoice marked as sent", detail: "This project's Xero invoice is now flagged as sent to the client." }
      : action === "update"
      ? { title: "Xero invoice updated successfully", detail: `Invoice ${project.xero_invoice_id ? `#${project.xero_invoice_id.slice(0, 8)}` : ""} synced to Xero.` }
      : { title: "Xero invoice created successfully", detail: `Invoice ${project.xero_invoice_id ? `#${project.xero_invoice_id.slice(0, 8)}` : ""} synced to Xero.` };
    return (
      <div className="flex items-start gap-3 bg-emerald-50 border border-emerald-200 rounded-sm px-5 py-4" data-testid="xero-sync-banner-success">
        <CheckCircle size={15} className="text-emerald-600 flex-shrink-0 mt-0.5" weight="fill" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-emerald-800">{successText.title}</p>
          <p className="text-xs text-emerald-600 mt-0.5">{successText.detail}</p>
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="flex items-start gap-3 bg-rose-50 border border-rose-200 rounded-sm px-5 py-4" data-testid="xero-sync-banner-error">
        <Warning size={15} className="text-rose-600 flex-shrink-0 mt-0.5" weight="fill" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-rose-800">Xero sync failed</p>
          <p className="text-xs text-rose-600 mt-0.5">{project.xero_sync_error || "Could not sync this invoice to Xero."}</p>
        </div>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            disabled={retrying}
            data-testid="xero-retry-sync-btn"
            className="flex items-center gap-1.5 text-xs font-semibold text-rose-700 bg-white border border-rose-300 rounded px-3 py-1.5 hover:bg-rose-100 transition-colors disabled:opacity-50 flex-shrink-0"
          >
            {retrying ? <ArrowsClockwise size={11} className="animate-spin" /> : null}
            {retrying ? "Retrying…" : "Retry Sync"}
          </button>
        )}
      </div>
    );
  }

  return null;
}

export function BackfillBanner({ project, msConnected, onTrigger, onDismiss }) {
  const status = project.backfill_status;
  const progress = project.backfill_progress || {};
  const summary = project.backfill_summary || {};
  const hasEmails = (project.client_emails || []).length > 0;
  const canTrigger = msConnected && hasEmails && !!project.onedrive_folder_id && status !== "running";

  if (!hasEmails || !msConnected) return null;

  if (status === "running") {
    const stage = progress.stage || "searching_inbox";
    const stageLabel = {
      searching_inbox: "Searching inbox…",
      scoring_emails: `Reviewing ${progress.total ?? "…"} emails with AI…`,
      processing_emails: `Processing email ${(progress.processed ?? 0) + 1} of ${progress.total ?? "…"}…`,
    }[stage] || "Working…";

    return (
      <div className="flex items-start gap-3 bg-violet-50 border border-violet-200 rounded-sm px-5 py-4" data-testid="backfill-banner-running">
        <ArrowsClockwise size={15} className="text-violet-500 animate-spin flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-violet-800">AI is sorting previous emails</p>
          <p className="text-xs text-violet-600 mt-0.5">{stageLabel}</p>
          {progress.current_subject && <p className="text-[10px] text-violet-500 mt-1 truncate">“{progress.current_subject}”</p>}
        </div>
      </div>
    );
  }

  if (status === "done" && summary.emails_matched != null) {
    const processed = summary.emails_processed ?? 0;
    const matched = summary.emails_matched ?? 0;
    const errors = summary.errors ?? 0;

    if (matched === 0) {
      return (
        <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-sm px-5 py-3" data-testid="backfill-banner-done-empty">
          <MagnifyingGlass size={13} className="text-slate-400 flex-shrink-0" />
          <p className="text-xs text-slate-500 flex-1">Inbox scanned — no emails matched this project.</p>
          <button onClick={onDismiss} className="text-slate-400 hover:text-slate-600"><X size={13} /></button>
        </div>
      );
    }

    return (
      <div className="flex items-start gap-3 bg-emerald-50 border border-emerald-200 rounded-sm px-5 py-4" data-testid="backfill-banner-done">
        <CheckCircle size={15} className="text-emerald-600 flex-shrink-0 mt-0.5" weight="fill" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-emerald-800">Inbox sync complete</p>
          <p className="text-xs text-emerald-700 mt-0.5">
            Found <span className="font-semibold">{matched}</span> matching email{matched !== 1 ? "s" : ""},
            processed <span className="font-semibold">{processed}</span>
            {errors > 0 && ` (${errors} error${errors !== 1 ? "s" : ""})`}.
            Files saved to project folder and drawings sorted.
          </p>
        </div>
        <button onClick={onDismiss} className="text-emerald-500 hover:text-emerald-700 flex-shrink-0"><X size={13} /></button>
      </div>
    );
  }

  if (status === "failed") {
    return (
      <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-sm px-5 py-4" data-testid="backfill-banner-failed">
        <Warning size={15} className="text-red-500 flex-shrink-0 mt-0.5" weight="fill" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-red-800">Inbox sync failed</p>
          <p className="text-xs text-red-600 mt-0.5">{summary.error || "An unexpected error occurred."}</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {canTrigger && <button onClick={onTrigger} className="text-xs font-medium text-red-600 hover:text-red-800 underline" data-testid="backfill-retry-btn">Retry</button>}
          <button onClick={onDismiss} className="text-red-400 hover:text-red-600"><X size={13} /></button>
        </div>
      </div>
    );
  }

  return null;
}

export function SmartUploadCard({ project, msConnected, pendingFiles }) {
  const [open, setOpen] = useState(false);
  const [panelKey, setPanelKey] = useState(0);
  const sectionRef = useRef(null);
  const hasFolder = !!project.onedrive_folder_id;

  useEffect(() => {
    if (pendingFiles?.length > 0) {
      setOpen(true);
      setPanelKey((k) => k + 1);
      setTimeout(() => {
        sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 80);
    }
  }, [pendingFiles]);

  if (!msConnected || !hasFolder) return null;

  return (
    <section ref={sectionRef} data-testid="smart-upload-section">
      {!open ? (
        <div className="bg-white border border-slate-200 rounded-sm p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-sm bg-violet-50 border border-violet-100 flex items-center justify-center flex-shrink-0">
                <CloudArrowUp size={15} className="text-violet-500" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-slate-800">Smart Upload</h3>
                <p className="text-xs text-slate-400 mt-0.5">AI automatically sorts drawings and files into the right folders</p>
              </div>
            </div>
            <Button size="sm" onClick={() => setOpen(true)} className="bg-violet-600 hover:bg-violet-700 text-white border-violet-600 flex-shrink-0" data-testid="smart-upload-open-btn">
              <Sparkle size={13} className="mr-1.5" weight="fill" />
              Upload Files
            </Button>
          </div>
        </div>
      ) : (
        <SmartUploadPanel key={panelKey} project={project} initialFiles={pendingFiles || []} onClose={() => setOpen(false)} onDone={() => {}} />
      )}
    </section>
  );
}

export function OneDriveCard({ project, msConnected, creatingFolder, onCreateFolder, onBrowse }) {
  return (
    <section className="bg-white border border-slate-200 rounded-sm p-5" data-testid="onedrive-section">
      <header className="flex items-center gap-2 mb-3">
        <FolderSimple size={14} className="text-[#0078D4]" weight="fill" />
        <h3 className="text-[10px] font-bold uppercase tracking-widest text-slate-500">OneDrive Folder</h3>
      </header>
      {project.onedrive_folder_url ? (
        <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-100 rounded-sm">
          <FolderSimple size={18} className="text-[#0078D4] flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-800 truncate">{project.onedrive_folder_name || "Project Folder"}</p>
            <p className="text-xs text-slate-400">Linked to OneDrive</p>
          </div>
          <Button size="sm" variant="outline" onClick={onBrowse} className="text-xs text-[#0078D4] border-[#0078D4]/30 hover:bg-blue-50 flex-shrink-0" data-testid="open-onedrive-folder-link">
            Browse Files <ArrowRight size={11} className="ml-1" />
          </Button>
        </div>
      ) : (
        <div className="flex items-center gap-3 p-3 border border-dashed border-slate-300 rounded-sm bg-slate-50">
          <FolderSimple size={18} className="text-slate-300 flex-shrink-0" />
          <p className="text-xs text-slate-400 flex-1">No OneDrive folder linked.</p>
          {msConnected === null ? (
            <ArrowsClockwise size={12} className="animate-spin text-slate-400" />
          ) : msConnected ? (
            <Button size="sm" variant="outline" onClick={onCreateFolder} disabled={creatingFolder} className="text-xs h-7" data-testid="create-folder-btn">
              {creatingFolder ? <ArrowsClockwise size={12} className="mr-1 animate-spin" /> : <Plus size={12} className="mr-1" />}
              Create Folder
            </Button>
          ) : (
            <span className="text-[10px] text-slate-400 italic">Grant server access to create</span>
          )}
        </div>
      )}
    </section>
  );
}

export function TrainingLogCard({ projectId }) {
  const [entries, setEntries] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [removing, setRemoving] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getTrainingLog(projectId);
      setEntries(data);
    } catch {
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (expanded && entries === null) load();
  }, [expanded, entries, load]);

  const handleRemove = async (entry) => {
    setRemoving(entry.id);
    try {
      await removeTrainingEntry(projectId, entry.id);
      setEntries((prev) => prev.filter((e) => e.id !== entry.id));
      toast.success(`Removed "${entry.learned_email}" from auto-routing list`);
    } catch {
      toast.error("Failed to remove entry.");
    } finally {
      setRemoving(null);
    }
  };

  const sourceLabel = (src) => (src === "user_resolved" ? "You assigned" : "Auto-learned");
  const sourceBadge = (src) => (src === "user_resolved" ? "bg-blue-50 text-blue-600 border-blue-100" : "bg-violet-50 text-violet-600 border-violet-100");

  return (
    <section className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="training-log-card">
      <button className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-slate-50 transition-colors" onClick={() => setExpanded((v) => !v)} data-testid="training-log-toggle">
        <div className="flex items-center gap-2">
          <Brain size={14} className="text-violet-500" weight="fill" />
          <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">AI Training Log</span>
          {entries !== null && entries.length > 0 && (
            <span className="inline-flex items-center justify-center px-1.5 py-0.5 text-[9px] font-bold rounded-full bg-violet-100 text-violet-700">
              {entries.length}
            </span>
          )}
        </div>
        <CaretRight size={12} className={`text-slate-400 transition-transform ${expanded ? "rotate-90" : ""}`} />
      </button>

      {expanded && (
        <div className="border-t border-slate-100">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-6 text-slate-400">
              <ArrowsClockwise size={13} className="animate-spin" />
              <span className="text-xs">Loading…</span>
            </div>
          ) : entries === null || entries.length === 0 ? (
            <div className="px-5 py-5 text-center">
              <ShieldCheck size={24} className="text-slate-300 mx-auto mb-2" />
              <p className="text-xs font-medium text-slate-500">No emails auto-learned yet</p>
              <p className="text-[11px] text-slate-400 mt-1 leading-relaxed">
                When the AI routes an email to this project and the sender is new,
                their address is automatically saved here.
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-slate-50" data-testid="training-log-list">
              {entries.map((entry) => (
                <li key={entry.id} className="group flex items-start gap-3 px-5 py-3 hover:bg-slate-50 transition-colors" data-testid={`training-log-entry-${entry.id}`}>
                  <Envelope size={13} className="text-slate-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-slate-800 truncate">{entry.learned_email}</p>
                    {entry.trigger_subject && <p className="text-[11px] text-slate-500 mt-0.5 truncate">via: {entry.trigger_subject}</p>}
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <span className={`inline-flex items-center px-1.5 py-0.5 text-[9px] font-semibold rounded border ${sourceBadge(entry.source)}`}>
                        {sourceLabel(entry.source)}
                      </span>
                      {entry.confidence != null && entry.source !== "user_resolved" && (
                        <span className="text-[10px] text-slate-400">{Math.round(entry.confidence * 100)}% confidence</span>
                      )}
                      <span className="text-[10px] text-slate-400">{fmtRelativeLog(entry.learned_at)}</span>
                    </div>
                  </div>
                  <button onClick={() => handleRemove(entry)} disabled={removing === entry.id} className="flex-shrink-0 opacity-0 group-hover:opacity-100 flex items-center gap-1 text-[10px] font-medium text-slate-400 hover:text-red-600 transition-all disabled:opacity-50 px-1.5 py-1 rounded hover:bg-red-50" title="Remove from auto-routing list" data-testid={`remove-training-entry-${entry.id}`}>
                    {removing === entry.id ? <ArrowsClockwise size={11} className="animate-spin" /> : <Trash size={11} />}
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="px-5 py-2.5 bg-slate-50/80 border-t border-slate-100">
            <p className="text-[10px] text-slate-400 leading-relaxed">
              Removing an email prevents future auto-routing from that address.
              It does not affect already-saved emails in OneDrive.
            </p>
          </div>
        </div>
      )}
    </section>
  );
}


export function RoutingDecisionsSection({ projectId }) {
  const [expanded, setExpanded] = useState(false);
  const [traces, setTraces] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!expanded || !projectId) return;
    setLoading(true);
    getRoutingTraces(projectId, 20)
      .then((data) => setTraces(Array.isArray(data) ? data : []))
      .catch(() => setTraces([]))
      .finally(() => setLoading(false));
  }, [expanded, projectId]);

  return (
    <section data-testid="routing-decisions-section">
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-2 px-4 py-3 bg-white border border-slate-200 rounded-sm hover:bg-slate-50 transition-colors"
        aria-expanded={expanded}
        data-testid="routing-decisions-toggle"
      >
        <div className="w-7 h-7 rounded-sm bg-indigo-50 border border-indigo-100 flex items-center justify-center flex-shrink-0">
          <ShieldCheck size={14} className="text-indigo-500" />
        </div>
        <span className="text-sm font-semibold text-slate-800">Routing Decisions</span>
        <CaretRight size={12} className={`ml-auto text-slate-400 transition-transform ${expanded ? "rotate-90" : ""}`} />
      </button>

      {expanded && (
        <div className="bg-white border border-t-0 border-slate-200 rounded-b-sm px-4 py-3 space-y-2">
          {loading && <p className="text-xs text-slate-400">Loading routing decisions…</p>}
          {!loading && traces.length === 0 && (
            <p className="text-xs text-slate-400">No routing decisions recorded for this project yet.</p>
          )}
          {!loading && traces.map((trace) => (
            <div key={trace.id} className="border border-slate-100 rounded px-3 py-2 space-y-1" data-testid={`routing-trace-${trace.id}`}>
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-slate-700 truncate max-w-[60%]">
                  {trace.subject || "(no subject)"}
                </span>
                <span className="text-[10px] text-slate-400">
                  {trace.created_at ? new Date(trace.created_at).toLocaleDateString("en-AU") : ""}
                </span>
              </div>
              <div className="flex items-center gap-2 text-[10px] text-slate-500">
                <span>From: {trace.from_email || "unknown"}</span>
                <span className={`px-1.5 py-0.5 rounded font-medium ${
                  trace.outcome === "completed" ? "bg-emerald-50 text-emerald-600" :
                  trace.outcome === "awaiting" ? "bg-amber-50 text-amber-600" :
                  trace.outcome === "skipped" ? "bg-slate-100 text-slate-500" :
                  "bg-red-50 text-red-600"
                }`}>
                  {trace.outcome || "unknown"}
                </span>
                {trace.accuracy_labels?.was_overridden && (
                  <span className="px-1.5 py-0.5 rounded bg-orange-50 text-orange-600 font-medium">overridden</span>
                )}
              </div>
              {trace.routing_trace?.decider_output?.reasoning && (
                <p className="text-[10px] text-slate-400 mt-1 leading-relaxed">
                  {trace.routing_trace.decider_output.reasoning}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
